/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type ContentStackFeaturedSpotlight } from '../../interface';
import NextImage, { blurDataURLPlaceholder } from '@/components/NextImage';
import styles from './CMSFeaturedSpotlight.module.scss';
import { Grid } from '@/components/Grid';
import { CMSLink } from '../CMSLink/CMSLink';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { CMSHeadline } from '../CMSHeadline/CMSHeadline';
import cx from 'classnames';

export type CMSFeaturedSpotlightProps = {
  content: ContentStackFeaturedSpotlight;
};

export function CMSFeaturedSpotlight({ content }: CMSFeaturedSpotlightProps) {
  const { headline, layout, feature_content, mobile_horizontal_scroll_disabled } = content;
  const isHorizontalScrollEnabled = !mobile_horizontal_scroll_disabled;

  const expandedLayout = layout === 'Expanded';

  return (
    <div>
      {headline ? <CMSHeadline>{headline}</CMSHeadline> : null}

      <Grid
        container
        data-testid="featured-spotlight"
        spacing={3}
        className={cx({ [styles.scrollContainer]: isHorizontalScrollEnabled })}
      >
        {feature_content?.map(({ link, feature_icon: icon, heading, sub_heading }, i) => {
          const iconSrc = icon?.image_url;
          const iconAlt = icon?.image_alt_text;

          const iconComponent = (
            <NextImage
              className={styles.image}
              alt={iconAlt}
              src={iconSrc ?? blurDataURLPlaceholder}
              width={60}
              height={60}
            />
          );

          return (
            <Grid
              key={i}
              item
              md={4}
              xs={12}
              className={isHorizontalScrollEnabled ? styles.unsetPadding : ''}
            >
              <div
                className={cx(styles.itemContainer, {
                  [styles.expandedLayout]: expandedLayout,
                  [styles.scrollCard]: isHorizontalScrollEnabled,
                })}
              >
                {!expandedLayout && !isHorizontalScrollEnabled ? iconComponent : null}
                {isHorizontalScrollEnabled ? (
                  <>
                    {iconComponent}
                    <div className={styles.descriptionContainer}>
                      <CMSLink className={styles.title} to={link}>
                        {heading}
                      </CMSLink>
                      <p className={styles.subTitle}>{sub_heading}</p>
                    </div>
                  </>
                ) : (
                  <div className={styles.descriptionContainer}>
                    <FlexGrid alignItems="center" gap={4}>
                      {expandedLayout ? iconComponent : null}
                      <CMSLink className={styles.title} to={link}>
                        {heading}
                      </CMSLink>
                    </FlexGrid>
                    <p className={styles.subTitle}>{sub_heading}</p>
                  </div>
                )}
              </div>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
}
